import { useEffect, useMemo, useState } from 'react';
import { useGetIngredientsQuery } from '../features/quizApiSlice';
import { getIngredientMonthlyPrice } from '../utils/productSuggestions';
import useFormulaEditor from './useFormulaEditor';

const useComparisonFormulaDraft = ({ comparison, ingredients, pricing, lang }) => {
  const [data, setData] = useState(() => ingredients?.map((item) => ({ ...item })) || []);
  const [hasFormulaChanged, setHasFormulaChanged] = useState(false);
  const [error, setError] = useState({});
  const [, setPrices] = useState({});
  const { data: allIngredients, isLoading: isLoadingCatalog, isError: isCatalogError } = useGetIngredientsQuery(lang);
  const storageKey = comparison
    ? `comparison-draft:${comparison.customerId}:${comparison.quiz_id}:${comparison.new_quiz_id}`
    : null;
  const originalIngredients = ingredients || comparison?.ingredients2;
  const formulaPricing = pricing || comparison?.pricing;
  const customerId = comparison?.customerId;

  useEffect(() => {
    if (!storageKey) {
      setData(originalIngredients?.map((item) => ({ ...item })) || []);
      setHasFormulaChanged(false);
      setError({});
      return;
    }
    const prefix = `comparison-draft:${customerId}:`;
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(prefix) && key !== storageKey)
      .forEach((key) => sessionStorage.removeItem(key));
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(storageKey));
    } catch {
      saved = null;
    }
    setData(Array.isArray(saved) ? saved : originalIngredients);
    setHasFormulaChanged(Array.isArray(saved));
    setError({});
  }, [customerId, storageKey, originalIngredients]);

  const ingredientDraft = useMemo(
    () =>
      data.map((item) => ({
        ingredient_id: item.ingredient_id,
        daily_dose: item.daily_dose,
      })),
    [data],
  );
  const prices = useMemo(
    () =>
      Object.fromEntries(
        data.map((item) => [item.ingredient, getIngredientMonthlyPrice(item, item.daily_dose)]),
      ),
    [data],
  );

  useEffect(() => {
    if (!storageKey || !hasFormulaChanged) return;
    sessionStorage.setItem(storageKey, JSON.stringify(data));
  }, [data, hasFormulaChanged, storageKey]);

  const cadenceMonths = formulaPricing?.cadenceMonths;
  const monthlyAmount = Object.values(prices).reduce((sum, price) => sum + price, 0);
  const newAmount = [1, 3].includes(cadenceMonths)
    ? Math.round(monthlyAmount * (cadenceMonths === 3 ? 0.7 : 0.9) * cadenceMonths)
    : null;
  const isInvalid =
    Object.values(error).some(Boolean) ||
    !data.length ||
    !data.some((item) => Number(item.daily_dose) > 0) ||
    data.some((item) => !Number.isFinite(Number(item.daily_dose)) || Number(item.daily_dose) < 0);

  const editor = useFormulaEditor({
    setData,
    originalIngredients,
    setHasFormulaChanged,
    setError,
    setPrices,
    pricesData: prices,
    allIngredients,
  });
  const handleReset = () => {
    editor.resetFormula();
    if (storageKey) sessionStorage.removeItem(storageKey);
  };
  const clearDraft = () => {
    if (storageKey) sessionStorage.removeItem(storageKey);
  };
  return {
    ...editor,
    data,
    allIngredients,
    prices,
    hasFormulaChanged,
    ingredientDraft,
    handleReset,
    clearDraft,
    pricing: !comparison || hasFormulaChanged ? { ...formulaPricing, newAmount } : formulaPricing,
    isInvalid,
    isLoadingCatalog,
    isCatalogError,
  };
};

export default useComparisonFormulaDraft;
